import React from 'react';
import { handleImageError } from '../utils/imageFallback';
import useSwipe, { SWIPE_STYLE } from '../hooks/useSwipe';

const TreatmentCaseSlider = ({
  cases,
  currentSlide,
  setCurrentSlide,
  sliderHeight,
  slideRefs,
  gradientColors = 'from-blue-50 to-purple-50', // 기본 그라데이션 색상
  title = '치료 사례',
  showDuringImage = false, // 치료중 이미지 표시 여부
  showImageDescriptions = false, // 이미지 설명 표시 여부
  showOverallDescription = false // 전체 치료 과정 설명 표시 여부
}) => {
  // 옆으로 밀어서 다음/이전 사례로 이동 (사례가 2개 이상일 때만)
  const total = cases ? cases.length : 0;
  const swipeHandlers = useSwipe(
    () => total > 1 && setCurrentSlide(currentSlide < total - 1 ? currentSlide + 1 : 0),
    () => total > 1 && setCurrentSlide(currentSlide > 0 ? currentSlide - 1 : total - 1)
  );

  if (!cases || cases.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="max-w-2xl mx-auto">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-4">{title}</h3>
          <p className="text-lg text-gray-600 mb-8">
            치료 사례는 현재 준비 중입니다.
          </p>
          <div className="bg-blue-50 rounded-lg p-8">
            <p className="text-gray-700">곧 더 많은 치료 사례와 함께 찾아뵙겠습니다.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-3xl font-bold text-center text-primary mb-8">{title}</h3>
      
      {/* 슬라이더 컨테이너 */}
      <div className="relative max-w-5xl mx-auto">
        {/* 슬라이더 */}
        <div className="overflow-hidden rounded-2xl transition-all duration-500 ease-in-out" {...swipeHandlers} style={SWIPE_STYLE}>
          <div 
            className="flex transition-all duration-500 ease-in-out"
            style={{ 
              transform: `translateX(-${currentSlide * 100}%)`,
              height: sliderHeight
            }}
          >
            {cases.map((caseItem, index) => (
              <div key={caseItem.id} className="w-full flex-shrink-0">
                <div 
                  ref={(el) => slideRefs.current[index] = el}
                  className="bg-white shadow-lg mx-2 h-auto"
                >
                  {/* 사례 헤더 */}
                  <div className={`bg-gradient-to-r ${gradientColors} p-3 md:p-6`}>
                    <div className="flex items-center mb-3 md:mb-4">
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-primary rounded-full flex items-center justify-center mr-2 md:mr-3">
                        <span className="text-white font-bold text-sm md:text-base">{caseItem.id}</span>
                      </div>
                      <div>
                        <h4 className="text-sm md:text-lg font-bold text-primary">사례 {caseItem.id}</h4>
                        <p className="text-xs md:text-sm text-gray-600">환자연령: {caseItem.age}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                      <div className="bg-white rounded-lg p-2 md:p-3">
                        <h5 className="font-semibold text-gray-800 mb-1 text-xs md:text-sm">내원사유</h5>
                        <p className="text-gray-700 text-xs md:text-sm leading-relaxed">{caseItem.reason}</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 md:p-3">
                        <h5 className="font-semibold text-gray-800 mb-1 text-xs md:text-sm">치료내용</h5>
                        <p className="text-gray-700 text-xs md:text-sm">{caseItem.treatment}</p>
                      </div>
                    </div>
                  </div>

                  {/* 전후 사진 */}
                  <div className="p-3 md:p-6">
                    <div className={`grid gap-2 md:gap-6 ${showDuringImage && caseItem.duringImage ? 'grid-cols-3' : 'grid-cols-2'}`}>
                      {/* 치료 전 */}
                      <div className="text-center">
                        <h5 className="text-xs md:text-md font-semibold text-gray-800 mb-1 md:mb-3">치료 전</h5>
                        <div className="bg-gray-100 rounded-lg p-1 md:p-4">
                          <div className="w-full aspect-[4/3] mx-auto rounded-lg overflow-hidden shadow-md bg-white">
                            <img 
                              src={caseItem.beforeImage} 
                              alt={`사례 ${caseItem.id} 치료 전`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={handleImageError}
                            />
                          </div>
                        </div>
                        {showImageDescriptions && caseItem.beforeDescription && (
                          <p className="text-xs md:text-sm text-gray-600 mt-2 leading-relaxed">
                            {caseItem.beforeDescription}
                          </p>
                        )}
                      </div>

                      {/* 치료 중 (교정 동반 임플란트인 경우) */}
                      {showDuringImage && caseItem.duringImage && (
                        <div className="text-center">
                          <h5 className="text-xs md:text-md font-semibold text-secondary mb-1 md:mb-3">치료 중</h5>
                          <div className="bg-gray-100 rounded-lg p-1 md:p-4">
                            <div className="w-full aspect-[4/3] mx-auto rounded-lg overflow-hidden shadow-md bg-white">
                              <img 
                                src={caseItem.duringImage} 
                                alt={`사례 ${caseItem.id} 치료 중`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                onError={handleImageError}
                              />
                            </div>
                          </div>
                          {showImageDescriptions && caseItem.duringDescription && (
                            <p className="text-xs md:text-sm text-gray-600 mt-2 leading-relaxed">
                              {caseItem.duringDescription}
                            </p>
                          )}
                        </div>
                      )}

                      {/* 치료 후 */}
                      <div className="text-center">
                        <h5 className="text-xs md:text-md font-semibold text-gray-800 mb-1 md:mb-3">치료 후</h5>
                        <div className="bg-gray-100 rounded-lg p-1 md:p-4">
                          <div className="w-full aspect-[4/3] mx-auto rounded-lg overflow-hidden shadow-md bg-white">
                            <img 
                              src={caseItem.afterImage} 
                              alt={`사례 ${caseItem.id} 치료 후`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={handleImageError}
                            />
                          </div>
                        </div>
                        {showImageDescriptions && caseItem.afterDescription && (
                          <p className="text-xs md:text-sm text-gray-600 mt-2 leading-relaxed">
                            {caseItem.afterDescription}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* 추가 이미지들 (4개 사진이 있는 경우 2x2 그리드로 표시) */}
                    {(caseItem.beforeImage2 || caseItem.afterImage2) && (
                      <div className="mt-4 md:mt-6">
                        {/* beforeImage2와 afterImage2가 모두 있는 경우 2x2 그리드 */}
                        {caseItem.beforeImage2 && caseItem.afterImage2 && !caseItem.followUp ? (
                          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 md:gap-4">
                            <div className="text-center">
                              <h5 className="text-xs md:text-md font-semibold text-gray-800 mb-1 md:mb-3">치료 전 (추가)</h5>
                              <div className="bg-gray-100 rounded-lg p-1 md:p-4">
                                <div className="w-full aspect-[4/3] mx-auto rounded-lg overflow-hidden shadow-md bg-white">
                                  <img 
                                    src={caseItem.beforeImage2} 
                                    alt={`사례 ${caseItem.id} 치료 전 추가`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    onError={handleImageError}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="text-center">
                              <h5 className="text-xs md:text-md font-semibold text-gray-800 mb-1 md:mb-3">치료 후 (추가)</h5>
                              <div className="bg-gray-100 rounded-lg p-1 md:p-4">
                                <div className="w-full aspect-[4/3] mx-auto rounded-lg overflow-hidden shadow-md bg-white">
                                  <img 
                                    src={caseItem.afterImage2} 
                                    alt={`사례 ${caseItem.id} 치료 후 추가`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    onError={handleImageError}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* 개별 이미지들을 따로 표시 */
                          <>
                            {caseItem.beforeImage2 && (
                              <div className="mb-4 md:mb-6">
                                <div className="grid grid-cols-1 gap-2 md:gap-4">
                                  <div className="text-center">
                                    <h5 className="text-xs md:text-md font-semibold text-gray-800 mb-1 md:mb-3">치료 전 (추가)</h5>
                                    <div className="bg-gray-100 rounded-lg p-1 md:p-4">
                                      <div className="w-full aspect-[4/3] mx-auto rounded-lg overflow-hidden shadow-md bg-white">
                                        <img 
                                          src={caseItem.beforeImage2} 
                                          alt={`사례 ${caseItem.id} 치료 전 추가`}
                                          className="w-full h-full object-cover"
                                          loading="lazy"
                                          onError={handleImageError}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {caseItem.afterImage2 && (
                              <div className={`grid gap-2 md:gap-4 ${caseItem.followUp ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                <div className="text-center">
                                  <h5 className="text-xs md:text-md font-semibold text-gray-800 mb-1 md:mb-3">치료 후 (추가)</h5>
                                  <div className="bg-gray-100 rounded-lg p-1 md:p-4">
                                    <div className="w-full aspect-[4/3] mx-auto rounded-lg overflow-hidden shadow-md bg-white">
                                      <img 
                                        src={caseItem.afterImage2} 
                                        alt={`사례 ${caseItem.id} 치료 후 추가`}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                        onError={handleImageError}
                                      />
                                    </div>
                                  </div>
                                </div>
                                {caseItem.followUp && (
                                  <div className="text-center">
                                    <h5 className="text-xs md:text-md font-semibold text-secondary mb-1 md:mb-3">6년 후 경과</h5>
                                    <div className="bg-gray-100 rounded-lg p-1 md:p-4">
                                      <div className="w-full aspect-[4/3] mx-auto rounded-lg overflow-hidden shadow-md bg-white">
                                        <img 
                                          src={caseItem.followUp} 
                                          alt={`사례 ${caseItem.id} 6년 후`}
                                          className="w-full h-full object-cover"
                                          loading="lazy"
                                          onError={handleImageError}
                                        />
                                      </div>
                                    </div>
                                    <p className="text-xs text-secondary mt-1 md:mt-2 font-medium">
                                      ✨ 6년이 지나도 자연스럽게 유지되고 있습니다
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* 전체 치료 과정 설명 */}
                    {showOverallDescription && caseItem.overallDescription && (
                      <div className="mt-4 md:mt-6">
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 md:p-6">
                          <p className="text-sm md:text-base text-gray-700 leading-relaxed text-center">
                            {caseItem.overallDescription}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 좌우 네비게이션 버튼 (2개 이상일 때만 표시) */}
        {cases.length > 1 && (
          <>
            <button
              onClick={() => setCurrentSlide(currentSlide > 0 ? currentSlide - 1 : cases.length - 1)}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 bg-white rounded-full p-2.5 shadow-lg hover:bg-gray-50 transition-colors z-10"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={() => setCurrentSlide(currentSlide < cases.length - 1 ? currentSlide + 1 : 0)}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 bg-white rounded-full p-2.5 shadow-lg hover:bg-gray-50 transition-colors z-10"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* 슬라이드 인디케이터 (2개 이상일 때만 표시) */}
        {cases.length > 1 && (
          <div className="flex justify-center mt-6 space-x-2">
            {cases.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  currentSlide === index ? 'bg-primary' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        )}

        {/* 현재 슬라이드 정보 (2개 이상일 때만 표시) */}
        {cases.length > 1 && (
          <div className="text-center mt-4">
            <span className="text-sm text-gray-500">
              {currentSlide + 1} / {cases.length}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TreatmentCaseSlider; 